import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronRight,
  Compass,
  Globe,
  KeyRound,
  LogOut,
  Mail,
  Repeat,
  UserCog,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { sdk } from '@/lib/tesseraSdk';
import { cn } from '@/lib/cn';
import { TOUR_REGISTRY, toursForRoles } from '@/tours';
import { startTourLazy } from '@/lib/simulRuntime';
import { fetchMyRoles, switchRole as apiSwitchRole } from '@/api/roleSwitch';

const LANGUAGES = [
  { code: 'es', label: 'Español', flag: '/brand/lopettia/flags/es.svg' },
  { code: 'en', label: 'English', flag: '/brand/lopettia/flags/us.svg' },
  { code: 'pt-BR', label: 'Português', flag: '/brand/lopettia/flags/br.svg' },
];

type Submenu = 'lang' | 'roles' | 'tours' | null;

/**
 * Top-right user dropdown.
 *
 * Sections (top → bottom):
 *   1. Profile header — initials, role, user id, **Edit profile** link.
 *   2. Role row — non-interactive single role line OR **Switch role**
 *      submenu when the user holds multiple roles.
 *   3. Guided tour — single "Start guided tour" link when the user has
 *      exactly one role with a registered tour, otherwise a submenu
 *      listing each role's tour.
 *   4. Preferences — Language, Change password, Email settings.
 *   5. Logout.
 *
 * Multi-role data is loaded best-effort post-login via /auth/my-roles;
 * a tab-refresh after this UserMenu mounts re-fetches in case the
 * post-login attempt was racing the very first store hydration.
 */
export function UserMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setRoles = useAuthStore((s) => s.setRoles);
  const setCurrentRole = useAuthStore((s) => s.setCurrentRole);

  const [open, setOpen] = useState(false);
  const [submenu, setSubmenu] = useState<Submenu>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Re-fetch on first menu open in case the post-login fetch was
  // missed (network blip) or the persisted store still has the legacy
  // single-role envelope from a pre-115 build.
  const refetchedRef = useRef(false);
  useEffect(() => {
    if (!open || refetchedRef.current || !user) return;
    refetchedRef.current = true;
    fetchMyRoles()
      .then((res) => setRoles(res.roles))
      .catch(() => {
        /* non-fatal — store keeps existing roles list */
      });
  }, [open, user, setRoles]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSubmenu(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const roles = useMemo(
    () => user?.roles ?? (user?.role ? [user.role] : []),
    [user?.roles, user?.role],
  );
  const currentRole = user?.currentRole ?? user?.role ?? '';
  const availableTours = useMemo(() => toursForRoles(roles), [roles]);

  // Initials seeded from the active role — keeps the avatar coherent
  // when a user with multiple personas flips between them.
  const initials = (currentRole || '?')
    .split(/[_\s]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s: string) => s[0]?.toUpperCase() ?? '')
    .join('') || '?';

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  function go(path: string) {
    setOpen(false);
    setSubmenu(null);
    navigate(path);
  }

  async function onSwitchRole(role: string) {
    if (role === currentRole) {
      setSubmenu(null);
      return;
    }
    try {
      await apiSwitchRole(role);
      setCurrentRole(role);
    } catch {
      // Surface nothing in the UI today — a 403 means the role isn't
      // actually granted, which the menu would not have shown in the
      // first place. Falling through preserves the previous active
      // role and the submenu just closes.
    } finally {
      setSubmenu(null);
      setOpen(false);
    }
  }

  async function onStartTour(role: string) {
    const entry = TOUR_REGISTRY[role];
    if (!entry) return;
    setOpen(false);
    setSubmenu(null);
    await startTourLazy(entry.tour);
  }

  function handleLogout() {
    // Read the id_token from the persisted session so Tessera can
    // terminate the upstream WorkOS session. Without it, the AuthKit
    // cookie survives and the next /oauth2/authorize silently
    // re-authenticates the user (SDK comment + WorkOS docs).
    // Do NOT insert state cleanup between these lines — see the SDK's
    // logout() contract for why the ordering matters.
    const idTokenHint = sdk.getCurrentTokens()?.idToken;
    sdk.logout({ idTokenHint });
  }

  if (!user) return null;

  const multiRole = roles.length > 1;
  const tourCount = availableTours.length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => {
          setOpen((v) => !v);
          setSubmenu(null);
        }}
        data-testid="user-menu-trigger"
        data-tour-id="user-menu-trigger"
        aria-label={t('userMenu.open')}
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-lop-bg-card)] text-sm font-semibold text-[var(--color-lop-text-secondary)] transition-all',
          'border border-[var(--color-lop-border)] hover:border-[var(--color-lop-teal)] hover:bg-[var(--color-lop-bg-card-hi)] hover:text-[var(--color-lop-teal)]',
          open && 'border-[var(--color-lop-teal)] text-[var(--color-lop-teal)] shadow-[0_0_0_3px_var(--color-lop-teal-glow)]',
        )}
      >
        {initials}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-[10px] border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] shadow-[var(--shadow-lop-md)]">
          {/* Profile header */}
          <div className="border-b border-[var(--color-lop-border)] px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-lop-bg-card-hi)] text-sm font-semibold text-[var(--color-lop-teal)]">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-[var(--color-lop-text-primary)]">
                  {currentRole}
                </div>
                <div className="truncate font-mono text-[11px] uppercase tracking-wider text-[var(--color-lop-text-muted)]">
                  {user.userId}
                </div>
              </div>
            </div>
            <button
              onClick={() => go('/account/profile')}
              data-testid="user-menu-edit-profile"
              className="mt-3 flex w-full items-center gap-2 rounded-md border border-[var(--color-lop-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-lop-text-secondary)] transition-colors hover:border-[var(--color-lop-teal)] hover:text-[var(--color-lop-teal)]"
            >
              <UserCog className="h-3.5 w-3.5" />
              <span>{t('userMenu.editProfile')}</span>
            </button>
          </div>

          {/* Role row — single-role static line, multi-role submenu */}
          <div className="border-b border-[var(--color-lop-border)] py-1">
            {!multiRole ? (
              <div className="flex items-center gap-3 px-4 py-2 text-sm text-[var(--color-lop-text-muted)]">
                <Repeat className="h-4 w-4" />
                <span className="flex-1">{t('userMenu.currentRole')}</span>
                <span className="text-xs font-medium text-[var(--color-lop-text-secondary)]">
                  {currentRole}
                </span>
              </div>
            ) : (
              <>
                <SubMenu
                  label={t('userMenu.switchRole')}
                  value={currentRole}
                  icon={<Repeat className="h-4 w-4" />}
                  open={submenu === 'roles'}
                  onClick={() => setSubmenu(submenu === 'roles' ? null : 'roles')}
                  testId="user-menu-switch-role"
                />
                {submenu === 'roles' && (
                  <div className="bg-[var(--color-lop-bg-mid)]">
                    {roles.map((role) => (
                      <button
                        key={role}
                        onClick={() => onSwitchRole(role)}
                        data-testid={`user-menu-role-${role}`}
                        className={cn(
                          'flex w-full items-center gap-3 px-6 py-2 text-sm transition-colors',
                          role === currentRole
                            ? 'bg-[var(--color-lop-bg-card-hi)] font-medium text-[var(--color-lop-teal)]'
                            : 'text-[var(--color-lop-text-secondary)] hover:bg-[var(--color-lop-bg-card-hi)] hover:text-[var(--color-lop-text-primary)]',
                        )}
                      >
                        <span className="flex-1 text-left">{role}</span>
                        {role === currentRole && <Check className="h-3.5 w-3.5" />}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Guided tour row */}
          {tourCount > 0 && (
            <div className="border-b border-[var(--color-lop-border)] py-1">
              {tourCount === 1 ? (
                <MenuItem
                  icon={<Compass className="h-4 w-4" />}
                  label={t('userMenu.startTour')}
                  onClick={() => onStartTour(availableTours[0].role)}
                  testId="user-menu-start-tour"
                />
              ) : (
                <>
                  <SubMenu
                    label={t('userMenu.guidedTour')}
                    value={`${tourCount}`}
                    icon={<Compass className="h-4 w-4" />}
                    open={submenu === 'tours'}
                    onClick={() => setSubmenu(submenu === 'tours' ? null : 'tours')}
                    testId="user-menu-tours"
                  />
                  {submenu === 'tours' && (
                    <div className="bg-[var(--color-lop-bg-mid)]">
                      {availableTours.map(({ role, entry }) => (
                        <button
                          key={role}
                          onClick={() => onStartTour(role)}
                          data-testid={`user-menu-tour-${role}`}
                          className="flex w-full items-center gap-3 px-6 py-2 text-sm text-[var(--color-lop-text-secondary)] transition-colors hover:bg-[var(--color-lop-bg-card-hi)] hover:text-[var(--color-lop-text-primary)]"
                        >
                          <span>{t(entry.labelKey)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Language */}
          <SubMenu
            label={t('userMenu.language')}
            value={currentLang.label}
            icon={<Globe className="h-4 w-4" />}
            open={submenu === 'lang'}
            onClick={() => setSubmenu(submenu === 'lang' ? null : 'lang')}
          />
          {submenu === 'lang' && (
            <div className="border-b border-[var(--color-lop-border)] bg-[var(--color-lop-bg-mid)]">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    i18n.changeLanguage(lang.code);
                    localStorage.setItem('kairos.lang', lang.code);
                    setSubmenu(null);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 px-6 py-2 text-sm transition-colors',
                    lang.code === i18n.language
                      ? 'bg-[var(--color-lop-bg-card-hi)] font-medium text-[var(--color-lop-teal)]'
                      : 'text-[var(--color-lop-text-secondary)] hover:bg-[var(--color-lop-bg-card-hi)] hover:text-[var(--color-lop-text-primary)]',
                  )}
                >
                  <img
                    src={lang.flag}
                    alt=""
                    className="h-3 w-[18px] rounded-[2px] shadow-[0_0_0_1px_rgba(255,255,255,0.15)]"
                  />
                  <span>{lang.label}</span>
                </button>
              ))}
            </div>
          )}

          <div className="py-1">
            <MenuItem
              icon={<KeyRound className="h-4 w-4" />}
              label={t('userMenu.changePassword')}
              onClick={() => go('/account/change-password')}
              testId="user-menu-change-password"
            />
            <MenuItem
              icon={<Mail className="h-4 w-4" />}
              label={t('userMenu.emailSettings')}
              onClick={() => go('/account/email-settings')}
              testId="user-menu-email-settings"
            />
          </div>

          <div className="border-t border-[var(--color-lop-border)] py-1">
            <MenuItem
              icon={<LogOut className="h-4 w-4" />}
              label={t('userMenu.logout')}
              onClick={handleLogout}
              testId="user-menu-logout"
              destructive
            />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  testId,
  destructive,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  testId?: string;
  destructive?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--color-lop-text-secondary)] transition-colors',
        'hover:bg-[var(--color-lop-bg-card-hi)] hover:text-[var(--color-lop-text-primary)]',
        destructive && 'text-[var(--color-lop-danger)] hover:bg-[var(--color-lop-danger)]/10 hover:text-[var(--color-lop-danger)]',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function SubMenu({
  label,
  value,
  icon,
  open,
  onClick,
  testId,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  open: boolean;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--color-lop-text-secondary)] transition-colors hover:bg-[var(--color-lop-bg-card-hi)] hover:text-[var(--color-lop-text-primary)]"
    >
      {icon}
      <span className="flex-1 text-left">{label}</span>
      <span className="text-xs text-[var(--color-lop-text-muted)]">{value}</span>
      <ChevronRight
        className={cn(
          'h-3.5 w-3.5 text-[var(--color-lop-text-muted)] transition-transform',
          open && 'rotate-90',
        )}
      />
    </button>
  );
}
